import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OnboardingState } from '../config/state.model';
import { AuthService } from './auth.service';
import { colVal } from './payload-builder';

@Injectable({ providedIn: 'root' })
export class TurnkeyPlansApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private headers(token: string): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `JWT ${token}` });
  }

  async submit(state: OnboardingState, baseUrl: string): Promise<void> {
    const { token } = await this.auth.ensureAuth();
    const hdrs = this.headers(token);

    // Prefetch all templates once and build case-insensitive name → id map
    const raw = await firstValueFrom(
      this.http.get<any>(`${baseUrl}/turnkey-tracker/template-plan/`, { headers: hdrs })
    );
    // Response is grouped: [{category, templates: [{id, name, ...}]}, ...]
    const templateList: { id: number; name: string }[] = Array.isArray(raw)
      ? raw.flatMap((group: any) => group.templates ?? [])
      : (raw?.results ?? []);
    const templateMap: Record<string, number> = {};
    for (const t of templateList) {
      if (t.name) templateMap[t.name.trim().toLowerCase()] = t.id;
    }

    // Build one payload per row, skipping rows with no plan name
    const payloads = state.rows
      .map(row => {
        const name = colVal(state, 'name', row);
        if (!name) return null;
        const tplRaw = colVal(state, 'template_name', row) ?? '';
        const templateId = tplRaw.trim()
          ? (templateMap[tplRaw.trim().toLowerCase()] ?? null)
          : null;
        return {
          name,
          template_id:  templateId,
          start_date:   colVal(state, 'start_date', row)  || null,
          end_date:     colVal(state, 'end_date', row)    || null,
          description:  colVal(state, 'description', row) || null,
          status:       colVal(state, 'status', row)      || null,
          origin:       colVal(state, 'origin', row)      || null,
        };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);

    // POST each plan individually — the existing endpoint handles
    // template scaffolding (phases + activities) automatically per plan
    await Promise.all(
      payloads.map(payload =>
        firstValueFrom(
          this.http.post<any>(`${baseUrl}/turnkey-tracker/plan/`, payload, { headers: hdrs })
        )
      )
    );
  }
}
